/** Reusable animated card carousel — content, layout, styling, motion. */

export const TEMPLATE_ANIMATED_CARD_CAROUSEL = 'ANIMATED_CARD_CAROUSEL';

export const CARD_LAYOUTS = [
  { id: 'horizontal', label: 'Horizontal' },
  { id: 'vertical', label: 'Vertical' },
  { id: 'offset', label: 'Offset stack' },
  { id: 'grid', label: 'Grid' },
  { id: 'center', label: 'Center focus' },
];

export const CARD_ENTRANCES = [
  { id: 'popIn', label: 'Pop In' },
  { id: 'slideUp', label: 'Slide Up' },
  { id: 'slideLeft', label: 'Slide Left' },
  { id: 'slideRight', label: 'Slide Right' },
  { id: 'zoomIn', label: 'Zoom In' },
  { id: 'floatIn', label: 'Float In' },
  { id: 'bounceIn', label: 'Bounce In' },
  { id: 'flipIn', label: 'Flip In' },
  { id: 'blurIn', label: 'Blur In' },
  { id: 'none', label: 'None' },
];

export const CARD_EXITS = [
  { id: 'fadeOut', label: 'Fade Out' },
  { id: 'slideDown', label: 'Slide Down' },
  { id: 'zoomOut', label: 'Zoom Out' },
  { id: 'none', label: 'None' },
];

export const CARD_TEXT_ANIMS = [
  { id: 'fade', label: 'Fade' },
  { id: 'slide', label: 'Slide' },
  { id: 'scale', label: 'Scale' },
  { id: 'typewriter', label: 'Typewriter' },
  { id: 'word', label: 'Word-by-word' },
  { id: 'character', label: 'Character-by-character' },
  { id: 'highlight', label: 'Highlight reveal' },
];

export const CARD_IMAGE_FITS = [
  { id: 'cover', label: 'Cover' },
  { id: 'contain', label: 'Contain' },
  { id: 'crop', label: 'Crop' },
  { id: 'smart', label: 'Smart Crop' },
];

export const CARD_STYLES = {
  default: {
    borderRadius: 0.14,
    shadow: true,
    border: true,
    borderWidth: 2,
    titlePosition: 'top',
    imageFit: 'cover',
  },
  premium: {
    borderRadius: 0.18,
    shadow: true,
    border: true,
    borderWidth: 1,
    titlePosition: 'top',
    imageFit: 'cover',
    fontWeight: 800,
  },
  magazine: {
    borderRadius: 0.04,
    shadow: true,
    border: false,
    titlePosition: 'top',
    imageFit: 'cover',
    fontWeight: 700,
  },
};

export const DEFAULT_CAROUSEL_PARAMS = {
  template: TEMPLATE_ANIMATED_CARD_CAROUSEL,
  layout: 'horizontal',
  staggerDelay: 0.25,
  animationIn: 'popIn',
  animationOut: 'fadeOut',
  animationDuration: 0.4,
  titleAnim: 'fade',
  titlePosition: 'top',
  imageFit: 'cover',
  borderRadius: 0.14,
  shadow: true,
  gap: 0.024,
  scale: 1,
  visibleCount: 0,
  styleId: 'default',
};

export function clampCarouselParams(raw = {}) {
  const data = raw && typeof raw === 'object' ? raw : {};
  const layout = CARD_LAYOUTS.some((row) => row.id === data.layout) ? data.layout : 'horizontal';
  const animationIn = CARD_ENTRANCES.some((row) => row.id === data.animationIn) ? data.animationIn : 'popIn';
  const animationOut = CARD_EXITS.some((row) => row.id === data.animationOut) ? data.animationOut : 'fadeOut';
  const titleAnim = CARD_TEXT_ANIMS.some((row) => row.id === data.titleAnim) ? data.titleAnim : 'fade';
  const imageFit = CARD_IMAGE_FITS.some((row) => row.id === data.imageFit) ? data.imageFit : 'cover';
  const stagger = Number(data.staggerDelay);
  const animDur = Number(data.animationDuration);
  const gap = Number(data.gap);
  const scale = Number(data.scale);
  const radius = Number(data.borderRadius);
  const visible = Number(data.visibleCount);
  return {
    ...DEFAULT_CAROUSEL_PARAMS,
    ...data,
    layout,
    animationIn,
    animationOut,
    titleAnim,
    imageFit,
    staggerDelay: Math.min(1.2, Math.max(0, Number.isFinite(stagger) ? stagger : 0.25)),
    animationDuration: Math.min(1.2, Math.max(0.15, Number.isFinite(animDur) ? animDur : 0.4)),
    gap: Math.min(0.08, Math.max(0.008, Number.isFinite(gap) ? gap : 0.024)),
    scale: Math.min(1.6, Math.max(0.55, Number.isFinite(scale) ? scale : 1)),
    borderRadius: Math.min(0.4, Math.max(0, Number.isFinite(radius) ? radius : 0.14)),
    visibleCount: Math.max(0, Number.isFinite(visible) ? Math.round(visible) : 0),
    shadow: data.shadow !== false,
    styleId: CARD_STYLES[data.styleId] ? data.styleId : 'default',
  };
}

export function aspectRatioValue(aspectRatio = '9:16') {
  const raw = String(aspectRatio || '9:16');
  if (raw === '1:1') return 1;
  if (raw === '16:9') return 16 / 9;
  if (raw.includes(':')) {
    const [w, h] = raw.split(':').map(Number);
    if (w > 0 && h > 0) return w / h;
  }
  return 9 / 16;
}
